// TODO - Type 'any' needs to be fixed.
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import Fade from "@mui/material/Fade";
import Typography from "@mui/material/Typography";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { publishDraftShiftsAsync } from "../../../../../../export/shift-scheduler";
import Button from "../../../../../design/button";
import DialogWrapper from "../../../../../design/dialog-wrapper";
import { formatDate } from "../../../../../utils/helpers";
import useCanDirectAssign from "../../../controllers/use-can-direct-assign";
import useSchedulerData from "../../../controllers/use-scheduler-data";
import { resetPublishDraftShiftsState } from "../../../store/scheduler-slice";
import type { SelectedWorkerSlot } from "../../../types/direct-assignment";
import {
	isWithinRequiredWorkers,
	toWorkerSlots,
} from "../../../types/direct-assignment";
import JobItem from "../../job-role-item";
import AssignWorkersStep from "../direct-assign-workers/assign-workers-step";
import DirectAssignmentResults, {
	FailedAssignmentRow,
} from "../direct-assign-workers/direct-assignment-results";
import useDraftShiftWorkers from "../direct-assign-workers/use-draft-shift-workers";
import useReviewPublishShiftDialog from "./controllers/use-review-publish-shift-dialog";
import ReviewCheckbox from "./review-checkbox";
import ReviewPublishingShiftsTable from "./review-publish-shifts-table";
import { useReviewPublishColumns, useStyles } from "./utils";

type ReviewPublishingShiftsDialogProps = {
  open: boolean;
  handleClose: () => void;
};

const FADE_TIMEOUT = 225;

const getShiftTimeLabel = (row: any) =>
	`${formatDate({
		dateStr: row.shiftDateTime,
		formatType: "SHORT_DATE",
	})} ${row.time.start} - ${row.time.end}`;

const getJobRoleLabel = (row: any) =>
	row.jobRole ? (
		<JobItem jobRole={row.jobRole} />
	) : (
		(row.skills ?? [])
			.map((skill: { skillName: string }) => skill.skillName)
			.join(", ")
	);

export default function ReviewPublishingShiftsDialog({
  open,
  handleClose,
}: ReviewPublishingShiftsDialogProps) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { hasDirectAssignPermission, canDirectAssign } = useCanDirectAssign();

  // The draft row whose Workers are being picked; null on the Shift list
  const [assignShiftId, setAssignShiftId] = useState<number | null>(null);
  // Rows as they were when Publish was clicked. Published Shifts leave the
  // draft list, but the results screen still names them.
  const [publishedRows, setPublishedRows] = useState<any[]>([]);

  const {
    publishDraftShiftsAsyncErrorMessage,
    publishDraftShiftsAsyncStatus,
    publishDirectAssignmentResults,
    hasPublishDirectAssignmentFailures,
    checkAllowedToPublishData,
    checkAllowedToPublishStatus,
    checkAllowedToPublishErrorMessage,
    isShiftPurposeEnabled,
    shiftsData,
    filterAllWorkcenters,
    filterAllLocations,
    isWorkerSubTypeEnabled,
  } = useSchedulerData();

  const [
    {
      state,
      rows,
      amISure,
      timeLeftByJobs,
      workerCountByShift,
      workerCountByShiftStatus,
      reviewPublishShiftIds,
      directAssignments,
    },
    { handleShiftSelection, setAmISure, resetShiftCount, saveDirectAssignment },
  ] = useReviewPublishShiftDialog();

  const assignedCountByShift = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(directAssignments as { [shiftId: number]: SelectedWorkerSlot[] })
          .map(([shiftId, selection]) => [shiftId, selection.length])
      ),
    [directAssignments]
  );

  const selectedShiftIds = useMemo(() => [...(state ?? [])], [state]);

  const handleOpenAssignWorkers = useCallback((shiftId: number) => {
    setAssignShiftId(shiftId);
  }, []);

  const columns = useReviewPublishColumns({
    hasDirectAssignPermission,
    canDirectAssign,
    assignedCountByShift,
    selectedShiftIds: selectedShiftIds,
    onAssignWorkers: handleOpenAssignWorkers,
  });

  const { data: draftShiftWorkers, isLoading: isDraftShiftWorkersLoading } =
    useDraftShiftWorkers(assignShiftId);

  const assignRow = useMemo(
    () => (rows ?? []).find((row: any) => row.id === assignShiftId) ?? null,
    [rows, assignShiftId]
  );

  const assignShift = useMemo(() => {
    if (!assignRow) return null;
    const shift = (shiftsData ?? []).find(
      (draft: { id: number }) => draft.id === assignRow.id
    );
    if (!shift) return null;
    const workcenter = (filterAllWorkcenters ?? []).find(
      (wc: { id: number }) => wc.id === shift.workCenterId
    );
    const location = (filterAllLocations ?? []).find(
      (l: { id: number }) => l.id === workcenter?.locationId
    );
    return {
      startDateTime: shift.startDateTime,
      endDateTime: shift.endDateTime,
      numberOfWorkersNeeded: Number(shift.numberOfWorkersNeeded),
      jobId: shift.jobId,
      shiftWorkcenterName: workcenter?.name ?? "",
      shiftLocationName: location?.name ?? "",
      shiftLocationClusterName: draftShiftWorkers?.locationCluster ?? null,
      shiftAllocationType: draftShiftWorkers?.shiftAllocationType ?? "",
      isPartialSignupEnabled:
        draftShiftWorkers?.isPartialSignupEnabled ??
        !!shift.isPartialSignupEnabled,
      isFlexiSignupEnabled:
        draftShiftWorkers?.isFlexiSignupEnabled ?? !!shift.isFlexiSignupEnabled,
      minFlexiSignupHours: shift.minFlexiSignupHours,
      isOverlappigSignupEnabled:
        draftShiftWorkers?.isOverlappigSignupEnabled ?? false,
      overlappingSignupLimitMins:
        draftShiftWorkers?.overlappingSignupLimitMins ?? 0,
      isShiftOpenForOtherLocation:
        draftShiftWorkers?.isShiftOpenForOtherLocation ?? false,
    };
  }, [
    assignRow,
    shiftsData,
    filterAllWorkcenters,
    filterAllLocations,
    draftShiftWorkers,
  ]);

  const handleCancelAssign = useCallback(() => {
    setAssignShiftId(null);
  }, []);

  const handleSaveAssign = useCallback(
    (selection: SelectedWorkerSlot[]) => {
      if (assignShiftId) saveDirectAssignment(assignShiftId, selection);
      setAssignShiftId(null);
    },
    [assignShiftId, saveDirectAssignment]
  );

  const isShowingResults =
    publishDraftShiftsAsyncStatus === "fulfilled" &&
    hasPublishDirectAssignmentFailures;

  const closeDialog = () => {
    if (isShowingResults) dispatch(resetPublishDraftShiftsState());
    handleClose();
    if (resetShiftCount) {
      resetShiftCount();
    }
  };

  const selectedAssignments = useMemo(
    () =>
      (state ?? [])
        .filter((shiftId: number) => (directAssignments[shiftId] ?? []).length > 0)
        .map((shiftId: number) => ({
          shiftId,
          selection: directAssignments[shiftId] as SelectedWorkerSlot[],
        })),
    [state, directAssignments]
  );

  // The picker already caps and dedupes; this guards the request itself.
  // Required Workers caps each half, so a 1-Worker Shift can take one Worker
  // per half.
  const areAssignmentsValid = selectedAssignments.every(
    ({ shiftId, selection }: { shiftId: number; selection: SelectedWorkerSlot[] }) => {
      const row = (rows ?? []).find((r: any) => r.id === shiftId);
      const shift = (shiftsData ?? []).find(
        (draft: { id: number }) => draft.id === shiftId
      );
      const workerIds = selection.map((selected) => selected.workerId);
      return (
        !!row &&
        !!shift &&
        isWithinRequiredWorkers(selection, shift) &&
        new Set(workerIds).size === workerIds.length
      );
    }
  );

  const handlePublish = () => {
    if (!state) return;
    setPublishedRows(rows ?? []);
    if (selectedAssignments.length === 0) {
      dispatch(publishDraftShiftsAsync(state));
      return;
    }
    dispatch(
      publishDraftShiftsAsync({
        shiftIds: state,
        directAssignments: selectedAssignments.map(
          ({ shiftId, selection }: { shiftId: number; selection: SelectedWorkerSlot[] }) => ({
            shiftId,
            workerSlots: toWorkerSlots(selection),
          })
        ),
      })
    );
  };

  const failedRows: FailedAssignmentRow[] = useMemo(() => {
    if (!isShowingResults) return [];
    return (publishDirectAssignmentResults ?? [])
      .filter((result) => !result.isSuccess)
      .map((result, index) => {
        const row = publishedRows.find(
          (r: any) => Number(r.id) === Number(result.shiftId)
        );
        const picked = (directAssignments[Number(result.shiftId)] ?? []).find(
          (selected: SelectedWorkerSlot) => selected.workerId === result.workerId
        );
        return {
          key: `${result.shiftId}-${result.workerId ?? "shift"}-${index}`,
          shiftTimeLabel: row ? getShiftTimeLabel(row) : "",
          jobRoleLabel: row ? getJobRoleLabel(row) : "",
          workerName: result.workerId ? picked?.workerName ?? "" : "",
          reason: result.reason ?? "",
        };
      });
  }, [
    isShowingResults,
    publishDirectAssignmentResults,
    publishedRows,
    directAssignments,
  ]);

  const isStateEmpty = state ? state.length < 1 : true;
  const tableData = rows ? rows : [];
  // There is no column selector here, so the Purpose Details column earns its
  // width only when at least one Shift on screen has a purpose that asks for a
  // detail — or already carries an answer from before the question was removed.
  const hasAnyPurposeDetail = tableData.some(
    (row: any) =>
      !!row.purposeDetailLabel ||
      (!!row.shiftPurposeDetail && row.shiftPurposeDetail !== "-")
  );
  const hiddenFields = [
    ...(!isShiftPurposeEnabled ? ["shiftPurpose"] : []),
    ...(!isShiftPurposeEnabled || !hasAnyPurposeDetail
      ? ["shiftPurposeDetail"]
      : []),
  ];
  const finalColumns = hiddenFields.length
    ? columns.filter((col) => !hiddenFields.includes(col.field))
    : columns;

  const publishedShiftCount = publishedRows.filter((row: any) =>
    (state ?? []).includes(row.id)
  ).length;
  const assignedWorkerCount = (publishDirectAssignmentResults ?? []).filter(
    (result) => result.isSuccess && !!result.workerId
  ).length;

  const shiftListBody = (
    <DialogWrapper
      dialogTitle={t("scheduler_publish-and-notify-shifts")}
      dialogContent={
        <ReviewPublishingShiftsTable
          rows={tableData}
          columns={finalColumns}
          handleShiftSelection={handleShiftSelection}
          checkAllowedToPublishData={checkAllowedToPublishData}
          checkAllowedToPublishStatus={checkAllowedToPublishStatus}
          selectedShifts={state}
          timeLeftByJobs={timeLeftByJobs}
          workerCountByShift={workerCountByShift}
          workerCountByShiftStatus={workerCountByShiftStatus}
        />
      }
      contentStyles={{
        padding: "45px 45px 0px 45px",
      }}
      actionButtons={
        <>
          {/* Showing I am sure only if it is opened through header button */}
          {rows && reviewPublishShiftIds.length === 0 && (
            <ReviewCheckbox amISure={amISure} setAmISure={setAmISure} />
          )}
          {publishDraftShiftsAsyncErrorMessage ? (
            <Typography color="secondary">
              {publishDraftShiftsAsyncErrorMessage}.
            </Typography>
          ) : null}
          {!publishDraftShiftsAsyncErrorMessage &&
          checkAllowedToPublishStatus === "error" &&
          checkAllowedToPublishErrorMessage ? (
            <Typography color="secondary">
              {checkAllowedToPublishErrorMessage}
            </Typography>
          ) : null}
          <Button
            color="primary"
            variant="text"
            onClick={closeDialog}
            customStyles={{ marginRight: "16px" }}
          >
            {t("common_cancel")}
          </Button>
          <Button
            onClick={handlePublish}
            color="primary"
            variant="contained"
            disabled={
              publishDraftShiftsAsyncStatus === "pending" ||
              publishDraftShiftsAsyncStatus === "error" ||
              amISure === false ||
              isStateEmpty ||
              !areAssignmentsValid
            }
          >
            {publishDraftShiftsAsyncStatus === "pending" ? (
              <div className={classes.btnLabel}>
                <CircularProgress
                  size={25}
                  className={classes.progressIndicator}
                />
                {t("scheduler_publishing-shifts")}
              </div>
            ) : (
              t("scheduler_publish-and-notify")
            )}
          </Button>
        </>
      }
    />
  );

  const assignStepBody = assignRow ? (
    <AssignWorkersStep
      // A fresh picker per Shift, seeded with what was saved for it
      key={`${assignRow.id}-${draftShiftWorkers ? "loaded" : "loading"}`}
      shift={assignShift}
      workers={draftShiftWorkers?.workers ?? []}
      isLoading={isDraftShiftWorkersLoading || !draftShiftWorkers}
      initialSelection={directAssignments[assignRow.id] ?? []}
      isWorkerSubTypeEnabled={!!isWorkerSubTypeEnabled}
      shiftTimeLabel={getShiftTimeLabel(assignRow)}
      jobRoleLabel={getJobRoleLabel(assignRow)}
      shiftDetailsLabel={[
        assignShift?.shiftLocationName,
        assignShift?.shiftWorkcenterName,
        t("scheduler_n-required-workers", { count: Number(assignRow.workersRequired) }),
      ]
        .filter(Boolean)
        .join(" · ")}
      subtitle={t("scheduler_assign-workers-subtitle", {
        n: Number(assignRow.workersRequired),
      })}
      onCancel={handleCancelAssign}
      onSave={handleSaveAssign}
    />
  ) : null;

  const resultsBody = (
    <DirectAssignmentResults
      title={
        publishedShiftCount === 1
          ? t("scheduler_shift-published")
          : t("scheduler_shifts-published")
      }
      summary={t("scheduler_published-summary", {
        shifts: publishedShiftCount,
        workers: assignedWorkerCount,
      })}
      failedRows={failedRows}
      onDone={closeDialog}
    />
  );

  const bodyKey = isShowingResults
    ? "results"
    : assignStepBody
    ? `assign-${assignShiftId}`
    : "shifts";

  return (
    <Dialog
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          closeDialog();
        }
      }}
      aria-labelledby="add-new-shift"
      open={open}
      maxWidth={false}
      PaperProps={{ sx: { minWidth: "80vw" } }}
      TransitionProps={{
        onEntered: () => {
          window.dispatchEvent(new Event("resize"));
        },
      }}
    >
      <Fade in key={bodyKey} timeout={FADE_TIMEOUT}>
        <div>
          {isShowingResults
            ? resultsBody
            : assignStepBody ?? shiftListBody}
        </div>
      </Fade>
    </Dialog>
  );
}
